import ApiRouteProvider from '../route'
import axios from '@axios'
import Cache from "@core/api/request/Cache";
import {SubstanceLists} from "@/libs/substance";
import {objectToGETParams} from "../route";
import {isSustainity} from "@/libs/application";
import {DocumentType, ProductIdentifiers, ProductNames} from "@/libs/product";

export default {
    getAttributes() {
        return Cache.item('ProductAttributes', () => {
            return axios
                .get(ApiRouteProvider.product.attributes())
                .then(response => {
                    if (isSustainity()) {
                        if (response.data.hasOwnProperty('ProductId')) {
                            response.data.ProductId = response.data.ProductId.filter(attr => {
                                if (attr.key === ProductIdentifiers.SCIP_NUMBER) {
                                    return false;
                                }
                                return attr.key !== ProductIdentifiers.SUPPLIERS_SCIP_NUMBER;
                            });
                        }
                        if (response.data.hasOwnProperty('ProductName')) {
                            response.data.ProductName = response.data.ProductName.filter(attr => {
                                return attr.key !== ProductNames.SCIP_NAME;
                            });
                        }
                    }
                    return response;
                });
        }).getPromise();
    },
    getCategory(id) {
        return Cache.item('ProductCategory_' + id, () => {
            return axios.get(ApiRouteProvider.product.category(id));
        }).getPromise();
    },
    getCategories(qs) {
        if (undefined === qs) {
            return Cache.item('AllProductCategories', () => {
                return axios.get(ApiRouteProvider.product.categories(), null);
            }).getPromise();
        }

        return axios.get(ApiRouteProvider.product.categories(), qs ? {params: {qs: qs}} : null);
    },
    getSendRequestEmailTemplates() {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/send-request-email/template');
    },
    getCategoryChildren(id) {
        return axios.get(ApiRouteProvider.product.categoryChildren(id));
    },

    searchPurchasedProducts(supplierId, qs) {
        return axios.get(ApiRouteProvider.product.searchPurchasedProducts(supplierId, qs));
    },

    searchIncludedProducts(qs) {
        return axios.get(ApiRouteProvider.product.searchIncludedProducts(qs));
    },

    searchProducts(query, limit = 15) {
        return axios.get(ApiRouteProvider.product.searchProducts(query, limit));
    },

    getProductSummaries(ids = []) {
        const normalizedIds = Array.from(new Set(ids
            .map(id => Number(id))
            .filter(id => Number.isInteger(id) && id > 0)));

        if (!normalizedIds.length) {
            return Promise.resolve({
                data: {
                    items: [],
                    missingIds: [],
                },
            });
        }

        return axios.post(ApiRouteProvider.product.productSummaries(), {
            ids: normalizedIds,
        });
    },

    getCompositionRoots(qs = '', page = 1, perPage = 10) {
        return axios.get(ApiRouteProvider.product.compositionRoots({
            qs,
            page,
            perPage
        }));
    },

    getGroups() {
        return Cache.item('ProductGroups', () => {
            return axios.get(ApiRouteProvider.product.groups());
        }).getPromise();
    },

    getProductsForProductOverview(page, perPage) {
        return axios.get(ApiRouteProvider.product.overview({
            page,
            perPage
        }));
    },

    productOverviewFilter(filters, page, perPage) {
        return axios.get(ApiRouteProvider.product.overviewFilter(filters, page, perPage))
    },

    add(product) {
        return axios.post(ApiRouteProvider.product.add(), product);
    },

    makeScipSubmission(id) {
        return axios.post(ApiRouteProvider.product.makeSubmission(id), {
            type: 'SCIP'
        });
    },

    getProductCard(id) {
        return axios.get(ApiRouteProvider.product.card(id));
    },

    getProduct(id) {
        return axios.get(ApiRouteProvider.product.product(id));
    },

    getChildren(id) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/' + id + '/children');
    },

    getSupplierDocuments(id) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/' + id + '/supplier-documents');
    },

    getOrganizationDocuments(id, documentType = null) {
        const query = objectToGETParams({documentType});

        return axios.get(ApiRouteProvider.product.organizationDocuments(id) + (query ? '?' + query : ''));
    },

    getOrganizationUnclassifiedDocuments(id) {
        return this.getOrganizationDocuments(id, DocumentType.UNCLASSIFIED_VERIFICATION);
    },

    getSupplierVerificationDocuments(id, documentType = null) {
        const query = objectToGETParams({documentType});

        return axios.get(ApiRouteProvider.product.supplierVerificationDocuments(id) + (query ? '?' + query : ''));
    },

    getSupplierUnclassifiedVerificationDocuments(id) {
        return this.getSupplierVerificationDocuments(id, DocumentType.UNCLASSIFIED_VERIFICATION);
    },

    saveSupplierVerificationDocuments(id, formData) {
        return axios.post(ApiRouteProvider.product.supplierVerificationDocuments(id), formData);
    },

    getSupplierVerificationReplyDocuments(id) {
        return axios.get(ApiRouteProvider.product.supplierVerificationReplyDocuments(id));
    },

    uploadSupplierUnclassifiedVerificationDocuments(id, files) {
        const formData = new FormData();

        files.forEach(file => {
            formData.append('documents[]', file);
        });

        return axios.post(ApiRouteProvider.product.supplierUnclassifiedVerificationDocuments(id), formData);
    },

    uploadOrganizationUnclassifiedDocuments(id, files) {
        const formData = new FormData();

        files.forEach(file => {
            formData.append('documents[]', file);
        });

        return axios.post(ApiRouteProvider.product.organizationUnclassifiedDocuments(id), formData);
    },

    getSCIPNotificationFacts(id) {
        return axios.get(ApiRouteProvider.product.scipNotificationFacts(id));
    },

    getSCIPNotificationPreview(id) {
        return axios.get(ApiRouteProvider.product.scipNotificationPreview(id));
    },

    getLegislationStatusExplanation(id, legislationId) {
        return axios.get(ApiRouteProvider.product.legislationStatusExplanation(id, legislationId));
    },

    getLegislationQuestionAnswers(id) {
        return axios.get(ApiRouteProvider.product.legislationQuestionAnswers(id));
    },

    getHierarchy(id) {
        return axios.get(ApiRouteProvider.product.hierarchy(id));
    },

    getSentRequests(filters, sorting, page, perPage) {
        return axios.get(ApiRouteProvider.product.getSentRequests({
            filters,
            sort: sorting,
            page,
            perPage
        }));
    },

    getSentRequest(id) {
        return axios.get(ApiRouteProvider.product.getSentRequest(id));
    },

    saveExternalResponse(id, response) {
        return axios.post(
            ApiRouteProvider.getBaseUrl() + '/product/sent/requests/' + id + '/external-response',
            response
        );
    },

    getForwardedRequests(receivedRequestId) {
        return axios.get(ApiRouteProvider.product.getCustomerRequestSentRequests(receivedRequestId));
    },

    getReceivedRequestForwardedRequests(receivedRequestId) {
        return axios.get(
            ApiRouteProvider.product.receivedRequestForwardedRequests(receivedRequestId)
        );
    },

    getReceivedRequestWorkflowState(receivedRequestId) {
        return axios.get(
            ApiRouteProvider.product.receivedRequestWorkflowState(receivedRequestId)
        );
    },

    updateReceivedRequestWorkflowState(receivedRequestId, workflowState) {
        return axios.put(
            ApiRouteProvider.product.receivedRequestWorkflowState(receivedRequestId),
            {workflowState}
        );
    },

    getCustomerRequest(id) {
        return axios.get(ApiRouteProvider.product.getCustomerRequest(id));
    },

    getBulkReplyCandidates(id) {
        return axios.get(ApiRouteProvider.product.getBulkReplyCandidates(id));
    },

    updateCustomerRequest(id, request) {
        return axios.put(ApiRouteProvider.product.updateCustomerRequest(id), request);
    },

    getCustomerRequests(filters, sorting, page, perPage) {
        return axios.get(ApiRouteProvider.product.getCustomerRequests({
            filters,
            sort: sorting,
            page,
            perPage
        }));
    },

    getSubmissions(filters, sorting, page, perPage) {
        return axios.get(ApiRouteProvider.product.getSubmissions({
            filters,
            sort: sorting,
            page,
            perPage
        }));
    },

    getSubmissionCard(id) {
        return axios.get(ApiRouteProvider.product.getSubmissionCard(id));
    },

    getComponentsToRequest(id) {
        return axios.get(ApiRouteProvider.product.componentsToRequest(id));
    },

    getConcernElements(id) {
        return axios.get(ApiRouteProvider.product.getConcernElements(id));
    },

    includeComponents(id, components) {
        return axios.post(ApiRouteProvider.product.includeComponents(id), {components: components});
    },

    updateComponent(id, component) {
        return axios.post(ApiRouteProvider.product.updateComponent(id, component.id), {
            numberOfUnits: component.numberOfUnits
        });
    },

    excludeComponent(id, componentId) {
        return axios.delete(ApiRouteProvider.product.excludeComponent(id, componentId));
    },

    convertType(id, type) {
        return axios.post(ApiRouteProvider.product.convertType(id), {type});
    },

    update(id, product) {
        return axios.put(ApiRouteProvider.product.update(id), product);
    },

    updateSubstanceListQuestionState(productId, listId, state) {
        const normalizedProductId = Number(productId);
        const normalizedListId = Number(listId);
        if (!Number.isInteger(normalizedProductId) || normalizedProductId <= 0) {
            return Promise.reject(new Error('Product id must be a positive integer'));
        }
        if (!Number.isInteger(normalizedListId) || normalizedListId <= 0) {
            return Promise.reject(new Error('Substance list id must be a positive integer'));
        }
        if (state !== true && state !== false && state !== null) {
            return Promise.reject(new Error('Substance list question state must be true, false, or null'));
        }

        return axios.put(
            ApiRouteProvider.product.substanceListQuestion(normalizedProductId, normalizedListId),
            {state},
        ).then(response => {
            if (!response || response.status !== 204) {
                throw new Error('Substance list question state update must return 204 No Content');
            }

            return response;
        });
    },

    patchIdentifiers(productId, identifiers = [], removeTypes = []) {
        const normalizedProductId = Number(productId);
        if (!Number.isInteger(normalizedProductId) || normalizedProductId <= 0) {
            return Promise.reject(new Error('Product id must be a positive integer'));
        }

        return axios.patch(
            ApiRouteProvider.product.identifiers(normalizedProductId),
            {identifiers, removeTypes},
        ).then(response => {
            if (!response || response.status !== 204) {
                throw new Error('Product identifier update must return 204 No Content');
            }

            return response;
        });
    },

    updateScipSettings(id, settings) {
        return axios.put(ApiRouteProvider.product.updateScipSettings(id), settings);
    },

    excludeFromScipNotification(id, product, parent) {
        return axios.post(ApiRouteProvider.product.excludeFromScipNotification(id), {
            product, parent
        })
    },

    unsetExclusionFromScipNotification(id, product, parent) {
        return axios.delete(ApiRouteProvider.product.unsetExclusionFromScipNotification(id, product, parent))
    },

    getSubstances(id) {
        return axios.get(ApiRouteProvider.product.getSubstances(id));
    },

    getFMD(id, cursor = null) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/' + id + '/fmd');
    },

    saveFMD(id, version, declarations) {
        return axios.post(ApiRouteProvider.getBaseUrl() + '/product/' + id + '/fmd', {
            version,
            declarations: declarations.map(d => {
                return {
                    id : d.id,
                    materialId: d.materialId || d.material.id,
                    component: d.component,
                    weight: d.weight,
                    comment: d.comment || null,
                    substances: d.substances.map(s => {
                        return {
                            substanceId: s.substanceId || s.substance.id,
                            weight: s.weight,
                            comment: s.comment || null
                        }
                    })
                }
            })
        });
    },

    sendRequest(id, request) {
        return axios.post(ApiRouteProvider.product.sendRequest(id), request);
    },

    initiateRequest(id, request) {
        return axios.post(ApiRouteProvider.getBaseUrl() + '/product/research', request);
    },

    approveRequest(id, comment = '', recipients = []) {
        return axios.post(ApiRouteProvider.product.approveRequest(id), {
            requests: [parseInt(id)],
            comment,
            recipients
        });
    },
    partiallyApproveRequest(id, comment = '', recipients = []) {
        return axios.post(ApiRouteProvider.getBaseUrl() + '/product/sent/requests/approve-partially', {
            requests: [parseInt(id)],
            comment,
            recipients
        });
    },

    rejectRequest(id, reason, recipients = []) {
        return axios.post(ApiRouteProvider.product.rejectRequest(id), {
            reason: reason,
            recipients
        });
    },

    forwardRequest(requestId, request) {
        return axios.post(ApiRouteProvider.product.forwardRequest(requestId), request);
    },

    getSubstanceState(id, legislations = []) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/' + id + '/substance-state?' + objectToGETParams({
            legislations: legislations
        }));
    },

    replyToRequest(requestId, declaration) {
        let declarationValidity = null;
        if (declaration.declarationValidity) {
            declarationValidity = Number.isInteger(declaration.declarationValidity) ? declaration.declarationValidity : declaration.declarationValidity.month;
        }
        const payload = {
            documents: declaration.documents.filter(d => d.id).map(d => d.id),
            internalComment: declaration.internalComment,
            customerInformation: declaration.customerInformation,
            declarationValidity: declarationValidity
        };
        if (Object.prototype.hasOwnProperty.call(declaration, 'authorizedToSubmit')) {
            payload.authorizedToSubmit = declaration.authorizedToSubmit === true;
        }
        if (Object.prototype.hasOwnProperty.call(declaration, 'allowInformationReuse')) {
            payload.allowInformationReuse = declaration.allowInformationReuse === true;
        }

        return axios.post(ApiRouteProvider.product.replyRequest(requestId), payload);
    },

    replyToRequestBulk(requestId, declaration, additionalRequestIds) {
        let declarationValidity = null;
        if (declaration.declarationValidity) {
            declarationValidity = Number.isInteger(declaration.declarationValidity) ? declaration.declarationValidity : declaration.declarationValidity.month;
        }
        const payload = {
            additionalRequestIds,
            documents: declaration.documents.filter(d => d.id).map(d => d.id),
            internalComment: declaration.internalComment,
            customerInformation: declaration.customerInformation,
            declarationValidity,
        };
        if (Object.prototype.hasOwnProperty.call(declaration, 'authorizedToSubmit')) {
            payload.authorizedToSubmit = declaration.authorizedToSubmit === true;
        }
        if (Object.prototype.hasOwnProperty.call(declaration, 'allowInformationReuse')) {
            payload.allowInformationReuse = declaration.allowInformationReuse === true;
        }

        return axios.post(ApiRouteProvider.product.replyRequestBulk(requestId), payload);
    },

    getReply(requestId) {
        return axios.get(ApiRouteProvider.product.getReply(requestId));
    },

    saveSubstances(id, included, excluded) {
        return axios.put(ApiRouteProvider.product.saveSubstances(id), {
            included: included,
            excluded: excluded,
        });
    },

    buildFormData(formData, data, parentKey) {
        if (data && typeof data === 'object' && !(data instanceof Date) && !(data instanceof File)) {
            Object.keys(data).forEach(key => {
                this.buildFormData(formData, data[key], parentKey ? `${parentKey}[${key}]` : key);
            });
        } else {
            const value = data == null ? '' : data;

            formData.append(parentKey, value);
        }
    },

    getDocuments(id, documentType = null) {
        const query = objectToGETParams({documentType});

        return axios.get(ApiRouteProvider.product.getDocuments(id) + (query ? '?' + query : ''));
    },

    getUnclassifiedVerificationDocuments(id) {
        return this.getDocuments(id, DocumentType.UNCLASSIFIED_VERIFICATION);
    },

    uploadUnclassifiedVerificationDocuments(id, files) {
        const formData = new FormData();

        files.forEach(file => {
            formData.append('documents[]', file);
        });

        return axios.post(ApiRouteProvider.product.unclassifiedVerificationDocuments(id), formData);
    },

    getReceivedRequestDocuments(id, documentType = null) {
        const query = objectToGETParams({documentType});

        return axios.get(ApiRouteProvider.product.receivedRequestDocuments(id) + (query ? '?' + query : ''));
    },

    getReceivedRequestUnclassifiedVerificationDocuments(id) {
        return this.getReceivedRequestDocuments(id, DocumentType.UNCLASSIFIED_VERIFICATION);
    },

    uploadReceivedRequestUnclassifiedVerificationDocuments(id, files) {
        const formData = new FormData();

        files.forEach(file => {
            formData.append('documents[]', file);
        });

        return axios.post(
            ApiRouteProvider.product.receivedRequestUnclassifiedVerificationDocuments(id),
            formData
        );
    },

    getReceivedRequestDocumentationLinks(id) {
        return axios.get(ApiRouteProvider.product.receivedRequestDocumentationLinks(id));
    },

    addReceivedRequestDocumentationLinks(id, urls) {
        return axios.post(
            ApiRouteProvider.product.receivedRequestDocumentationLinks(id),
            {urls}
        );
    },

    deleteDocument(pid, id) {
        return axios.delete(ApiRouteProvider.product.deleteDocument(pid, id));
    },

    downloadDocument(pid, id) {
        return axios({
            url: ApiRouteProvider.product.downloadDocument(pid, id),
            method: 'GET',
            responseType: 'blob',
        });
    },

    downloadRepliedDocument(requestId, id) {
        return axios({
            url: ApiRouteProvider.getBaseUrl() + '/product/sent/requests/' + requestId + '/reply/documents/' + id + '/download',
            method: 'GET',
            responseType: 'blob',
        });
    },

    remove(id) {
        return axios.delete(ApiRouteProvider.product.remove(id));
    },

    saveOrganizationDocuments(
        id,
        DoC,
        evidenceReports,
        compliantStatements,
        safeUseInstructions,
        dissemblingInstructions,
        isSafeUseApplicable,
        productDescription
    ) {
        //  todo refactor this
        const formData = new FormData();
        const safeUse = {
            description: safeUseInstructions.description
        };

        const dissembling = {
            description: dissemblingInstructions.description
        };

        if (safeUseInstructions.id) {
            safeUse.id = safeUseInstructions.id;
        }

        if (safeUseInstructions.document instanceof File) {
            safeUse.document = safeUseInstructions.document;
        }

        if (dissemblingInstructions.id) {
            dissembling.id = dissemblingInstructions.id;
        }

        if (dissemblingInstructions.document instanceof File) {
            dissembling.document = dissemblingInstructions.document;
        }

        const data = {
            declarationOfConformity: DoC.map(doc => {
                const document = {
                    legislations: doc.legislations.map(l => l instanceof Object ? l.id : l),
                    declarationDate: doc.declarationDate,
                    declarationValidityDate: doc.declarationValidityDate,
                };

                if (doc.document instanceof File) {
                    document.document = doc.document;
                }

                if (doc.id) {
                    document.id = doc.id;
                }

                return document;
            }),
            compliantStatement: compliantStatements.map(doc => {
                const document = {
                    legislations: doc.legislations.map(l => l instanceof Object ? l.id : l),
                    declarationDate: doc.declarationDate,
                    declarationValidityDate: doc.declarationValidityDate,
                };

                if (doc.document instanceof File) {
                    document.document = doc.document;
                }

                if (doc.id) {
                    document.id = doc.id;
                }

                return document;
            }),
            evidenceReports: evidenceReports.map(report => {
                const document = {
                    legislations: report.legislations.map(l => l instanceof Object ? l.id : l),
                    documentTypes: report.documentTypes.map(t => t instanceof Object ? t.id : t),
                };

                if (report.document instanceof File) {
                    document.document = report.document;
                }

                if (report.id) {
                    document.id = report.id;
                }

                return document;
            }),
        };

        if (safeUse.id || safeUse.description || safeUse.document) {
            data.safeUseInstructions = safeUse;
        }

        if (dissembling.id || dissembling.description || dissembling.document) {
            data.dissemblingInstructions = dissembling;
        }

        data.isSafeUseApplicable = isSafeUseApplicable ? 1 : 0;

        if (productDescription && productDescription.document) {
            const productDescriptionData = {
                document: productDescription.document
            };

            if (productDescription.id) {
                productDescriptionData.id = productDescription.id;
            }

            data.description = productDescriptionData;
        }

        this.buildFormData(formData, data);

        return axios.post(ApiRouteProvider.product.organizationDocuments(id), formData);
    },

    componentsSubstances(id, legislations) {
        if (legislations) {
            legislations = legislations.map(l => l.id);
        }
        return axios.get(ApiRouteProvider.product.componentsSubstances(id, legislations));
    },

    complexProductComposition(id, include = []) {
        return axios.get(ApiRouteProvider.product.complexComposition(id, include));
    },

    additionalMaterials() {
        return Cache.item('ProductAdditionalMaterials', () => {
            return axios.get(ApiRouteProvider.product.additionalMaterials());
        }).getPromise();
    },

    mixtureCategories() {
        return Cache.item('ProductMixtureCategories', () => {
            return axios.get(ApiRouteProvider.product.mixtureCategories());
        }).getPromise();
    },

    sendProductRequestEmail(productId) {
        return axios.post(ApiRouteProvider.product.sendProductRequestEmail(productId), {
            productId
        });
    },

    resendRequestEmail(productRequestId) {
        return axios.get(ApiRouteProvider.product.mixtureCategories());
    },

    sendApproveDeclarationMessage(method, productRequestId, name, options) {
        return axios.post(ApiRouteProvider.product.sendApproveDeclarationMessage(productRequestId), {
            method, name, options
        });
    },

    approveDeclaration(productRequestId, code) {
        return axios.post(ApiRouteProvider.product.approveDeclaration(productRequestId), {
            code
        });
    },

    getGeneralProductStatements(filters, sorting, page, perPage) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements?' + objectToGETParams({
            filters,
            sort: sorting,
            page,
            perPage
        }))
    },

    getAllGeneralProductStatements() {
        return Cache.item('GeneralProductStatements', () => {
            return axios.get(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements?' + objectToGETParams({
                page: 1,
                perPage: 256
            }))
        }).getPromise();
    },

    getGeneralProductStatement(id) {
        return axios.get(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements/' + id);
    },

    createGeneralProductStatement(data) {
        return axios.post(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements', data);
    },

    updateGeneralProductStatement(id, data) {
        return axios.put(ApiRouteProvider.getBaseUrl() + '/product/general-product-statements/' + id, data);
    },

    emailMessageExample() {
        return axios.get(ApiRouteProvider.getBaseUrl() +  '/product/email-message-example');
    }
};
